import React, { useState } from 'react';

const Home = () => {
    const [inputCode, setInputCode] = useState('');
    const [outputCode, setOutputCode] = useState('');

    const extractTextFromParagraph = (pElement) => {
        // Extract text content from paragraph, handling spans and other nested elements
        const textContent = pElement.replace(/<[^>]*>/g, '').trim();
        // Replace &nbsp; with regular spaces and normalize whitespace
        return textContent.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    };

    const transformCode = () => {
        let result = inputCode;

        // Step 1: Replace <a with <a target="_blank"
        result = result.replace(/<a(\s|>)/g, '<a target="_blank"$1');

        // Step 2: Transform code blocks between ###000### and ###xxx### in paragraphs
        const paragraphRegex = /<p[^>]*>([\s\S]*?)<\/p>/g;
        let codeBlocks = [];
        let currentBlock = null;
        let matches = [];

        // Find all paragraphs and check for code block markers
        while ((matches = paragraphRegex.exec(result)) !== null) {
            const fullMatch = matches[0];
            const content = matches[1];
            const textContent = extractTextFromParagraph(content);

            if (textContent.includes('###000###')) {
                // Start of a code block
                currentBlock = {
                    startIndex: matches.index,
                    endIndex: null,
                    codeLines: []
                };
            } else if (textContent.includes('###xxx###') && currentBlock) {
                // End of a code block
                currentBlock.endIndex = matches.index + fullMatch.length;
                codeBlocks.push(currentBlock);
                currentBlock = null;
            } else if (currentBlock) {
                // Inside a code block - add to code lines
                currentBlock.codeLines.push(textContent);
            }
        }

        // Replace code blocks with CodeHighlighter components
        if (codeBlocks.length > 0) {
            // Process from last to first to preserve indices
            for (let i = codeBlocks.length - 1; i >= 0; i--) {
                const block = codeBlocks[i];
                const codeContent = block.codeLines.join('\n\n');

                const before = result.substring(0, block.startIndex);
                const after = result.substring(block.endIndex);

                result = before + `<CodeHighlighter
                code={\`

${codeContent}
                   
                    \`} 
            />` + after;
            }
        }

        setOutputCode(result);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(outputCode).then(() => {
            alert('Output copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                    JSX Code Transformer
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Input Section */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold text-gray-700 mb-4">Input JSX Code</h2>
                        <textarea
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            className="w-full h-96 p-4 border border-gray-300 rounded-md font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Paste your JSX code here..."
                        />
                        <button
                            onClick={transformCode}
                            className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md transition duration-200"
                        >
                            Transform Code
                        </button>
                    </div>

                    {/* Output Section */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-700">Transformed JSX Code</h2>
                            {outputCode && (
                                <button
                                    onClick={copyToClipboard}
                                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md transition duration-200 text-sm"
                                >
                                    Copy Output
                                </button>
                            )}
                        </div>
                        <textarea
                            value={outputCode}
                            readOnly
                            className="w-full h-96 p-4 border border-gray-300 rounded-md font-mono text-sm resize-none bg-gray-50 focus:outline-none"
                            placeholder="Transformed code will appear here..."
                        />
                    </div>
                </div>

                {/* Instructions */}
                <div className="mt-8 bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Transformations Applied:</h3>
                    <div className="space-y-3 text-gray-600">
                        <div className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <p><strong>Anchor Tags:</strong> All <code className="bg-gray-100 px-1 rounded">&lt;a</code> tags are updated to <code className="bg-gray-100 px-1 rounded">&lt;a target="_blank"</code></p>
                        </div>
                        <div className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <p><strong>Code Blocks:</strong> Content between <code className="bg-gray-100 px-1 rounded">###000###</code> and <code className="bg-gray-100 px-1 rounded">###xxx###</code> markers in paragraphs are converted to CodeHighlighter components</p>
                        </div>
                        <div className="flex items-start">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <p><strong>Pattern Removal:</strong> Paragraphs containing the <code className="bg-gray-100 px-1 rounded">###000###</code> and <code className="bg-gray-100 px-1 rounded">###xxx###</code> patterns are automatically removed</p>
                        </div>
                    </div>
                </div>

                {/* Example */}
                <div className="mt-6 bg-blue-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">Example Input:</h3>
                    <pre className="bg-white p-4 rounded border text-sm overflow-x-auto">
                        {`<p>###000###</p>
<p>my_list = [1, "Hello", 3.14, True]</p>
<p>my_list.append("New Item")</p>
<p>my_list.remove(3.14)</p>
<p>print(my_list[1]) # "Hello"</p>
<p>print(my_list[1:3]) # Slice: ['Hello', True]</p>
<p>###xxx###</p>`}
                    </pre>

                    <h3 className="text-lg font-semibold text-blue-800 mb-3 mt-4">Example Output:</h3>
                    <pre className="bg-white p-4 rounded border text-sm overflow-x-auto">
                        {`<CodeHighlighter
                code={\`

my_list = [1, "Hello", 3.14, True]

my_list.append("New Item")

my_list.remove(3.14)

print(my_list[1]) # "Hello"

print(my_list[1:3]) # Slice: ['Hello', True]
                   
                    \`} 
            />`}
                    </pre>
                </div>
            </div>
        </div>
    );
}

export default Home;